import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";

/**
 * Blinterest section shell — a labelled scrollable strip with optional
 * "See all" link + desktop scroll arrows. Renders whatever children the
 * caller passes as horizontal snap cards.
 */
export default function BlinterestSection({
  title,
  icon,
  seeAllHref,
  seeAllLabel = "See all",
  testid,
  children,
  gridClassName = "",
  variant = "scroll", // "scroll" | "grid"
}) {
  const trackRef = useRef(null);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const width = el.clientWidth * 0.85;
    el.scrollBy({ left: dir === "left" ? -width : width, behavior: "smooth" });
  };

  return (
    <section className="mb-8 sm:mb-10 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 sm:mb-4 px-4 sm:px-6 lg:px-0">
        <h2 className="font-heading text-white text-lg sm:text-xl flex items-center gap-2">
          {icon && <span className="text-tbc-gold">{icon}</span>}
          {title}
        </h2>
        <div className="flex items-center gap-2">
          {variant === "scroll" && (
            <div className="hidden lg:flex items-center gap-1">
              <button
                type="button"
                onClick={() => scroll("left")}
                className="w-8 h-8 rounded-full border border-white/20 text-white/70 hover:text-tbc-gold hover:border-tbc-gold/60 flex items-center justify-center"
                aria-label={`Scroll ${title} left`}
                data-testid={testid ? `${testid}-scroll-left` : undefined}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scroll("right")}
                className="w-8 h-8 rounded-full border border-white/20 text-white/70 hover:text-tbc-gold hover:border-tbc-gold/60 flex items-center justify-center"
                aria-label={`Scroll ${title} right`}
                data-testid={testid ? `${testid}-scroll-right` : undefined}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          {seeAllHref && (
            <Link
              to={seeAllHref}
              className="text-tbc-gold text-xs sm:text-sm font-body hover:underline"
              data-testid={testid ? `${testid}-see-all` : undefined}
            >
              {seeAllLabel}
            </Link>
          )}
        </div>
      </div>

      {variant === "scroll" ? (
        <div className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-hidden px-4 sm:px-6 lg:px-0"
          >
            {children}
          </div>
        </div>
      ) : (
        <div className={`px-4 sm:px-6 lg:px-0 ${gridClassName}`}>{children}</div>
      )}
    </section>
  );
}
